import { useMemo, useRef } from 'react';
import { areObjectsEqual } from '../utils';

/**
 * Enhance useMemo to provide the previous value to the factory function, enabling
 * us to have the benefits of useMemo lifecycle, while still having access to the
 * previous value.
 */
export const useMemoWithPreviousValue = <TValue>(
  factory: (previousValue: TValue | undefined) => TValue,
  deps: Array<unknown>,
): TValue => {
  const previousValue = useRef<TValue | undefined>(undefined);

  const memoizedValue = useMemo(() => {
    const maybeNewValue = factory(previousValue.current);

    if (
      typeof maybeNewValue === 'object' &&
      typeof previousValue.current === 'object' &&
      areObjectsEqual(maybeNewValue, previousValue.current)
    ) {
      // Safeguard if value didn't actually change

      return previousValue.current;
    }

    previousValue.current = maybeNewValue;

    return maybeNewValue;
  }, deps);

  return memoizedValue;
};
